/**
 * AiChatPage — conversational AI assistant for educators.
 *
 * All AI requests are routed through the Worker endpoint
 * /functions/v1/ai_chat_message which proxies to LAAP.
 * No Anthropic API key is used in the frontend.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Bot, Send, Trash2 } from 'lucide-react';
import { useAiChat } from '../hooks/useAiChat';

// One stable chatId per page load is sufficient for a session-scoped chat.
const PAGE_CHAT_ID = crypto.randomUUID();

export default function AiChatPage() {
  const { messages, isStreaming, error, sendMessage, clearMessages } = useAiChat({
    chatId: PAGE_CHAT_ID,
  });

  const [input, setInput] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  // Scroll to the latest message as new content arrives.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function handleSend() {
    const trimmed = input.trim();
    if (!trimmed || isStreaming) return;
    setInput('');
    await sendMessage(trimmed);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <div className="flex flex-col h-full max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <Bot size={20} className="text-primary" />
          <h2 className="text-lg font-semibold">AI Assistant</h2>
        </div>
        {messages.length > 0 && (
          <button
            onClick={clearMessages}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-red-600"
            aria-label="Clear conversation"
          >
            <Trash2 size={14} />
            Clear
          </button>
        )}
      </div>

      {/* Message list */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && !isStreaming && (
          <div className="flex flex-col items-center justify-center h-full text-center text-gray-400 gap-3 py-16">
            <Bot size={40} />
            <p className="text-sm">
              Ask the AI assistant anything — marking rubrics, lesson plans, feedback
              suggestions, and more.
            </p>
          </div>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={[
                'max-w-[80%] rounded-lg px-4 py-2 text-sm whitespace-pre-wrap',
                msg.role === 'user'
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100',
              ].join(' ')}
            >
              {msg.content || (
                <span className="animate-pulse text-gray-400">▍</span>
              )}
            </div>
          </div>
        ))}

        {error && (
          <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="border-t px-4 py-3">
        <div className="flex gap-2 items-end">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isStreaming}
            rows={2}
            placeholder="Ask a question… (Enter to send, Shift+Enter for new line)"
            className="flex-1 resize-none rounded-md border px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-primary disabled:opacity-50"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isStreaming}
            className="flex items-center gap-1 rounded bg-primary px-4 py-2 text-sm text-white hover:bg-primary/90 disabled:opacity-40"
            aria-label="Send message"
          >
            <Send size={16} />
            {isStreaming ? 'Thinking…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}
